// Strip the bloat that Word, Google Docs, Outlook, Pages, etc. embed when
// users copy formatted text. Returns clean HTML that only contains the
// formatting our TipTap config actually supports (p, strong/b, em/i, u, ul,
// ol, li, br). Everything else — inline styles, font tags, MS-only namespaces,
// conditional comments, classnames — is stripped.
//
// Why a manual scrub instead of an off-the-shelf sanitizer?
//   - Keeps the deps slim (no DOMPurify / sanitize-html for ~15 lines of logic)
//   - We control exactly which tags survive, so the mobile renderer can never
//     hit unexpected markup
//   - Operates on the HTML string before TipTap parses it, so it's compatible
//     with the `transformPastedHTML` hook

const TAG_WHITELIST = new Set([
  "P", "STRONG", "B", "EM", "I", "U", "UL", "OL", "LI", "BR", "DIV",
]);

function isCommentNode(node) {
  return node.nodeType === 8; // Node.COMMENT_NODE
}

function stripNode(node) {
  // Comments → drop entirely
  if (isCommentNode(node)) {
    node.remove();
    return;
  }
  // Text → keep as-is
  if (node.nodeType !== 1) return;

  const tag = node.tagName;

  // Drop unwanted top-level tags entirely (style, script, meta, link, etc.)
  if (
    tag === "STYLE" ||
    tag === "SCRIPT" ||
    tag === "META" ||
    tag === "LINK" ||
    tag === "TITLE" ||
    tag === "HEAD" ||
    tag.startsWith("O:") ||  // <o:p>, MS-Office namespace
    tag.startsWith("W:") ||  // <w:WordDocument> etc.
    tag.startsWith("V:")     // VML <v:shape> etc.
  ) {
    node.remove();
    return;
  }

  // Tags we don't allow at all but whose CONTENTS we want to preserve
  // (e.g. <font color="red">x</font> → x; <span>x</span> → x)
  if (!TAG_WHITELIST.has(tag)) {
    // <a> currently isn't whitelisted because our toolbar doesn't expose links.
    // If you later add a Link toolbar item, whitelist "A" and keep the `href`
    // attribute below.
    const parent = node.parentNode;
    while (node.firstChild) parent.insertBefore(node.firstChild, node);
    node.remove();
    return;
  }

  // Strip all attributes — even `class`, `style`, `id`, `lang`, `dir`, MS-specific.
  // For our small whitelist we never need any attribute except `href` on <a>
  // (which isn't in the list yet).
  for (const attr of Array.from(node.attributes)) {
    node.removeAttribute(attr.name);
  }
}

function walkAndScrub(root) {
  // Use a TreeWalker that visits *element + comment* nodes only. We mutate
  // during traversal, so collect first and process afterwards.
  const queue = [];
  const walker = document.createTreeWalker(
    root,
    // NodeFilter constants: SHOW_ELEMENT=1, SHOW_COMMENT=128
    1 | 128,
    null,
  );
  let n = walker.nextNode();
  while (n) {
    queue.push(n);
    n = walker.nextNode();
  }
  // Process deepest-first so we don't re-process newly-promoted children
  for (const node of queue.reverse()) {
    stripNode(node);
  }
}

export function cleanPastedHtml(html) {
  if (!html || typeof html !== "string") return html;

  const container = document.createElement("div");
  container.innerHTML = html;

  walkAndScrub(container);

  // Normalize MS-Office's non-breaking spaces inside lists (they often look
  // like "&nbsp;&nbsp;&nbsp;" indentation hacks that Word inserts in place of
  // real <ul>/<ol> nesting).
  let cleaned = container.innerHTML
    .replace(/\u00A0/g, " ")        // NBSP → regular space
    .replace(/\s+/g, " ")           // collapse whitespace
    .replace(/<p>\s*<\/p>/g, "")    // drop fully-empty paragraphs
    .replace(/<div>\s*<\/div>/g, "")
    .replace(/<br\s*\/?>(\s*<br\s*\/?>)+/g, "<br>")  // collapse runs of <br>
    .trim();

  return cleaned;
}
